import { ChevronDownIcon, InfoIcon } from '@chakra-ui/icons';
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import CustomCheckboxIcon from 'components/Icons/CustomCheckboxIcon';
import { textStyles } from 'pages/ModelOverviewPage/Import/Helper';
import {
  getNoheadersStatus,
  getSelectedVersionDetails,
  getTableHeader,
  handleActionStepNext,
  resetMultipleTimePeriodValue,
  setSelectedAreas,
  setSingleTimePeriodValue,
  setTimeValueMap,
} from 'redux/DataImportSlice';
import { getModelTime } from 'redux/IndicatorPageSlice';

import ActionStepFooter from '../../ActionStepFooter';

import { loadTimeValueOptions, type TimeColumnMapping } from './timeRowOptions';

const validationSchema = Yup.object().shape({
  singleTimePeriodValue: Yup.string().required('Time value is required'),
});

interface Properties {
  setHelpModalSeleceted?: any;
  getFormValue?: any;
}

type TimePeriodFormValues = {
  singleTimePeriodValue: string;
  periodMappings: TimeColumnMapping[];
};

const applySavedMappings = (
  mappings: TimeColumnMapping[],
  savedMappings: Array<{ key: string; value: string | null }> | null | undefined,
  modelPeriods: string[],
) => {
  if (!Array.isArray(savedMappings)) return mappings;

  const validModelPeriods = new Set(modelPeriods.map(String));
  const savedByValue = new Map(
    savedMappings.map((mapping) => [mapping.key, mapping.value ?? null]),
  );

  return mappings.map((mapping) => {
    if (!savedByValue.has(mapping.key)) return mapping;

    const savedValue = savedByValue.get(mapping.key);
    if (!savedValue || !validModelPeriods.has(String(savedValue))) return mapping;

    return {
      ...mapping,
      value: savedValue,
      autoMapped: true,
    };
  });
};

const getMappedPeriodCount = (mappings: TimeColumnMapping[]) =>
  mappings.filter((mapping) => Boolean(mapping.value)).length;

const Rows = ({ setHelpModalSeleceted, getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const modelTime = useSelector(getModelTime);
  const isHeader = useSelector(getNoheadersStatus);
  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);
  const [selectedTimeColumn, setSelectedTimeColumn] = useState(
    getFormValue?.singleTimePeriodValue || '',
  );
  const [timeValueOptions, setTimeValueOptions] = useState<TimeColumnMapping[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [manualMapping, setManualMapping] = useState(false);
  const modelPeriods = useMemo(
    () => (Array.isArray(modelTime?.time_range) ? modelTime.time_range : []),
    [modelTime?.time_range],
  );

  const VStackContainer: any = VStack;
  const MenuTrigger: any = MenuButton;
  const BodyText: any = Text;
  const FormControlContainer: any = FormControl;
  const FlexContainer: any = Flex;
  const MenuContainer: any = Menu;
  const MenuOptions: any = MenuList;
  const MenuGroup: any = MenuOptionGroup;
  const MenuOption: any = MenuItemOption;

  useEffect(() => {
    if (isHeader) {
      const array: string[] = new Array(26)
        .fill(1)
        .map((_: string, index: number) => String.fromCodePoint(65 + index));
      const newHeaders = array
        .slice(0, headerNames.length)
        .map((item: string, headerIndex: number) => {
          return { current: `${headerNames[headerIndex]}`, modified: `Column ${item}` };
        });
      setCustomHeaders(newHeaders);
    } else {
      setCustomHeaders(
        headerNames.map((item: string) => {
          return { current: `${item}`, modified: `${item}` };
        }),
      );
    }
  }, [isHeader, headerNames]);

  useEffect(() => {
    setSelectedTimeColumn(getFormValue?.singleTimePeriodValue || '');
  }, [getFormValue?.singleTimePeriodValue]);

  useEffect(() => {
    let isCancelled = false;

    const loadTimeValues = async () => {
      if (
        !selectedVersion?.s3_file_url ||
        !headerNames?.length ||
        !selectedTimeColumn ||
        modelPeriods.length === 0
      ) {
        setTimeValueOptions([]);
        return;
      }

      setLoadingValues(true);
      setLoadError('');

      try {
        const options = await loadTimeValueOptions({
          s3FileUrl: selectedVersion.s3_file_url,
          headerNames,
          timeColumn: selectedTimeColumn,
          modelTimeRange: modelPeriods,
        });
        if (isCancelled) return;
        setTimeValueOptions(options);
        setLoadingValues(false);
      } catch {
        if (isCancelled) return;
        setLoadError('Unable to read the selected file for time period detection.');
        setLoadingValues(false);
      }
    };

    loadTimeValues();

    return () => {
      isCancelled = true;
    };
  }, [headerNames, modelPeriods, selectedTimeColumn, selectedVersion?.s3_file_url]);

  const initialFormValues = useMemo<TimePeriodFormValues>(() => {
    return {
      singleTimePeriodValue: selectedTimeColumn,
      periodMappings: applySavedMappings(
        timeValueOptions,
        getFormValue?.timeValueMap,
        modelPeriods,
      ),
    };
  }, [getFormValue?.timeValueMap, modelPeriods, selectedTimeColumn, timeValueOptions]);

  const staleSavedMappingCount = useMemo(() => {
    if (!Array.isArray(getFormValue?.timeValueMap)) return 0;

    const validModelPeriods = new Set(modelPeriods.map(String));
    return getFormValue.timeValueMap.filter(
      (mapping: { value?: string | null }) =>
        mapping?.value && !validModelPeriods.has(String(mapping.value)),
    ).length;
  }, [getFormValue?.timeValueMap, modelPeriods]);

  const getSelectedColumnOption = (value: string) => {
    const data = customHeaders.find((item: any) => item.current === value);
    return data?.modified;
  };

  const handleSubmit = async (
    form: TimePeriodFormValues,
    setSubmitting: (data: boolean) => void,
  ) => {
    const mappingsForPayload = (form.periodMappings || [])
      .filter((mapping) => mapping?.key && mapping?.value)
      .map((mapping) => ({
        key: mapping.key,
        value: mapping.value,
      }));

    await dispatch(setSingleTimePeriodValue(form));
    await dispatch(setTimeValueMap(mappingsForPayload.length > 0 ? mappingsForPayload : null));
    await dispatch(resetMultipleTimePeriodValue());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <VStackContainer display='flex' justify='center' w='100%' pt={3}>
      <BodyText color='black' {...textStyles} textAlign='start' width='100%'>
        Select the column which has the time periods for each row
      </BodyText>
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => {
          const periodMappings: TimeColumnMapping[] = values?.periodMappings || [];
          const mappedPeriodCount = getMappedPeriodCount(periodMappings);
          const hasUnmappedPeriods = periodMappings.some((mapping) => !mapping.value);
          const mappingControlsEnabled = manualMapping || hasUnmappedPeriods;
          const updatePeriodMapping = async (mappingIndex: number, period: string) => {
            const nextMappings = periodMappings.map((mapping, index) =>
              index === mappingIndex
                ? {
                    ...mapping,
                    value: period || null,
                    autoMapped: false,
                  }
                : mapping,
            );
            await setFieldValue('periodMappings', nextMappings);
          };

          return (
            <Form
              style={{
                width: '100%',
              }}
            >
              <FormControlContainer pt={2}>
                <MenuContainer closeOnSelect={true} isLazy>
                  <MenuTrigger
                    as={Button}
                    backgroundColor='white'
                    maxHeight='40px'
                    borderRadius='5px'
                    minWidth='140px'
                    textAlign='left'
                    padding='0 .8rem'
                    boxShadow='rgb(139, 137, 177) 0px 0px 0px 0.6px'
                    rightIcon={<ChevronDownIcon fontSize='md' />}
                    fontWeight='300'
                    fontSize='xs'
                    color='black.900'
                    w='100%'
                    border={
                      errors.singleTimePeriodValue ? '2px solid #fe6e6e' : '0.6px solid #8B89B1'
                    }
                  >
                    <BodyText me={3} noOfLines={1} height='fit-content'>
                      {getSelectedColumnOption(values?.singleTimePeriodValue) ?? (
                        <BodyText opacity={0.5}>Select column</BodyText>
                      )}
                    </BodyText>
                  </MenuTrigger>
                  <MenuOptions
                    height='fit-content'
                    maxHeight='20vh'
                    overflowY='auto'
                    className='scroll'
                  >
                    <MenuGroup
                      type='radio'
                      value={values?.singleTimePeriodValue}
                      onChange={async (role: any) => {
                        await setFieldValue('singleTimePeriodValue', role);
                        await setFieldValue('periodMappings', []);
                        setSelectedTimeColumn(role);
                        setManualMapping(false);
                        await dispatch(setSelectedAreas({ selectedColumnOption: role }));
                      }}
                    >
                      <MenuOption value=''>Select...</MenuOption>
                      {customHeaders.map((header: any) => (
                        <MenuOption key={header.current} value={header.current}>
                          {header.modified}
                        </MenuOption>
                      ))}
                    </MenuGroup>
                  </MenuOptions>
                </MenuContainer>
                <ErrorMessage name='singleTimePeriodValue' component='div' className='error' />
              </FormControlContainer>

              {values?.singleTimePeriodValue && !loadingValues && periodMappings.length === 0 && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  No date-like time period values were detected in the selected column.
                </BodyText>
              )}

              {loadError && (
                <BodyText color='red' fontSize='xs' pt={2}>
                  {loadError}
                </BodyText>
              )}

              {periodMappings.length > 0 && (
                <VStackContainer align='stretch' spacing={3} pt={4} w='100%'>
                  <FlexContainer gap={2} w='100%' py={2}>
                    <Checkbox
                      background='transparent'
                      border='2px solid black'
                      borderColor='black'
                      className='checkboxStyle'
                      icon={<CustomCheckboxIcon isChecked={mappingControlsEnabled} />}
                      isChecked={mappingControlsEnabled}
                      isDisabled={hasUnmappedPeriods}
                      onChange={(event) => setManualMapping(event.target.checked)}
                    />
                    <BodyText color='black' fontSize='0.9rem' fontFamily='Roboto Regular'>
                      Map manually ?
                    </BodyText>
                  </FlexContainer>

                  {hasUnmappedPeriods && (
                    <BodyText color='#B45309' fontSize='xs'>
                      Some detected values do not match the model timeline. Select target periods
                      below; unmapped rows will be skipped.
                    </BodyText>
                  )}

                  {staleSavedMappingCount > 0 && (
                    <BodyText color='#B45309' fontSize='xs'>
                      Some saved time period mappings are no longer in the current model timeline.
                      Please remap them before continuing.
                    </BodyText>
                  )}

                  <BodyText
                    color='#6562CF'
                    fontSize='lg'
                    fontFamily='regular_roboto'
                    fontWeight='400'
                    textAlign='start'
                    py={1}
                  >
                    Map the values into Blox
                  </BodyText>

                  <FlexContainer justifyContent='space-between' alignItems='center' w='100%'>
                    <BodyText
                      fontSize='1rem'
                      color='black'
                      fontFamily='regular_roboto'
                      fontWeight='600'
                    >
                      Time period in File
                    </BodyText>
                    <FlexContainer w='41%'>
                      <BodyText
                        fontSize='1rem'
                        color='black'
                        fontFamily='regular_roboto'
                        fontWeight='600'
                      >
                        Time period in Blox
                      </BodyText>
                    </FlexContainer>
                  </FlexContainer>
                  <FlexContainer
                    direction='column'
                    className='scroll'
                    maxH='25vh'
                    overflowY='auto'
                    gap={2}
                  >
                    {periodMappings.map((mapping, index) => (
                      <FlexContainer
                        key={`${mapping.key}-${index}`}
                        alignItems='center'
                        justifyContent='space-between'
                        pt={3}
                        w='100%'
                      >
                        <FlexContainer alignItems='center' justifyContent='start' w='45%'>
                          <BodyText color='black' fontSize='xs' noOfLines={1}>
                            {mapping.sourceValue || mapping.key}
                          </BodyText>
                        </FlexContainer>
                        <FlexContainer alignItems='center' gap={2} justifyContent='end' w='50%'>
                          <BsArrowRight color='black' style={{ width: '20px', height: '20px' }} />
                          <MenuContainer closeOnSelect={true} isLazy>
                            <MenuTrigger
                              as={Button}
                              backgroundColor='white'
                              border='0.6px solid #8B89B1'
                              borderRadius='5px'
                              boxShadow='rgb(139, 137, 177) 0px 0px 0px 0.6px'
                              color='black.900'
                              fontSize='xs'
                              fontWeight='300'
                              isDisabled={!mappingControlsEnabled}
                              maxHeight='40px'
                              minWidth='140px'
                              padding='0 .8rem'
                              rightIcon={<ChevronDownIcon fontSize='md' />}
                              textAlign='left'
                              w='100%'
                            >
                              <BodyText me={3} noOfLines={1} opacity={mapping.value ? 1 : 0.5}>
                                {mapping.value || 'Select period'}
                              </BodyText>
                            </MenuTrigger>
                            <MenuOptions
                              className='scroll'
                              height='fit-content'
                              maxHeight='20vh'
                              overflowY='auto'
                            >
                              <MenuGroup
                                type='radio'
                                value={mapping.value || ''}
                                onChange={(period: any) => updatePeriodMapping(index, period)}
                              >
                                <MenuOption value=''>Skip this value</MenuOption>
                                {modelPeriods.map((period: string) => (
                                  <MenuOption key={period} value={period}>
                                    {period}
                                  </MenuOption>
                                ))}
                              </MenuGroup>
                            </MenuOptions>
                          </MenuContainer>
                        </FlexContainer>
                      </FlexContainer>
                    ))}
                  </FlexContainer>
                </VStackContainer>
              )}

              <FlexContainer
                alignItems='center'
                justifyContent='space-between'
                pb={8}
                pt='5'
                w='100%'
              >
                <FlexContainer>
                  <BodyText
                    {...textStyles}
                    color='black'
                    textDecoration='underline'
                    onClick={() => {
                      setHelpModalSeleceted.on();
                    }}
                    display='none'
                  >
                    Help <InfoIcon color='#8B89B1' ms={3} />
                  </BodyText>
                </FlexContainer>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                  disableNext={
                    !values?.singleTimePeriodValue ||
                    loadingValues ||
                    periodMappings.length === 0 ||
                    mappedPeriodCount === 0
                  }
                />
              </FlexContainer>
            </Form>
          );
        }}
      </Formik>
    </VStackContainer>
  );
};

export default Rows;
